import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { etiquetaArticulo, useIdentidades } from '../lib/identidadArticulos';
import { COLOR_RESULTADO, n, nombrePublicacion, plural, sincronizarEnLotes, type Progreso, type Resultado } from '../lib/sincronizarEnLotes';

/**
 * "Packs sin sincronizar de SKU que ya sincronizan" (DECISIONES 2026-10-10). Un pack nuevo de un SKU que ya sincroniza
 * (p. ej. el que crea la planilla 02_CODIGOS al corregir la columna E) no empieza solo: hasta sincronizarlo, Mercado Libre
 * se queda con el stock viejo. Los que pasan de un SKU que sincroniza a su pack empiezan solos con la sincronización de la
 * planilla; acá quedan los demás. Lista paginada y filtrable (sin leer ML); sincronizar es "Sincronizar también sus
 * packs" de cada SKU base (todos sus packs pendientes juntos), de a lotes de 100 con el reintento ante el límite de
 * pedidos. Sólo admin; el resto ve la lista.
 */

const URL = '/api/v3/ml/sync-sku';
const POR_PAGINA = 50;

interface PublicacionPack {
  mlaId: string;
  variantId: string | null;
  titulo: string | null;
  stockMl: number | null;
  sePublica: number | null;
  seEscribe: boolean;
  motivo: string | null;
}

interface FilaPack {
  sku: string;
  descripcion: string | null;
  base: string;
  unidadesPorPack: number;
  packsDeLaBase: string[];
  publicacionesActivas: number;
  publicacionesQueSeEscriben: number;
  publicaciones: PublicacionPack[];
  sePublica: number | null;
  contado: boolean;
  avisos: string[];
}

interface BaseFiltrada { sku: string; packs: string[]; publicacionesQueSeEscriben: number }

interface Listado { total: number; pagina: number; porPagina: number; filas: FilaPack[]; bases: BaseFiltrada[] }

interface ResultadoLote {
  resultados: Array<{ sku: string; estado: 'ok' | 'revisar' | 'error'; packs: string[]; mensaje: string | null }>;
}

export function PacksSinSincronizar({ esAdmin }: { esAdmin: boolean }) {
  const [listado, setListado] = useState<Listado | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  /** SKU base elegidos con lo que se confirma (sus packs) y cuántas publicaciones se escribirían. */
  const [elegidas, setElegidas] = useState<Map<string, BaseFiltrada>>(new Map());
  const [confirmar, setConfirmar] = useState(false);
  const [progreso, setProgreso] = useState<Progreso | null>(null);
  const [resultados, setResultados] = useState<Record<string, Resultado>>({});
  const [resumen, setResumen] = useState<string | null>(null);
  const corriendo = progreso !== null;
  const montado = useRef(true);
  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);

  /** Número del último pedido: la respuesta de uno anterior se descarta (2026-10-12). */
  const ultimoPedido = useRef(0);
  const cargar = useCallback(async () => {
    const pedido = ++ultimoPedido.current;
    setCargando(true);
    try {
      const params = new URLSearchParams({ ...(filtro ? { q: filtro } : {}), pagina: String(pagina), porPagina: String(POR_PAGINA) });
      const r = await api.get<Listado>(`${URL}/packs-sin-sincronizar?${params}`);
      if (pedido !== ultimoPedido.current) return;
      // La lista se achicó (p. ej. después de sincronizar): se pide la última página.
      const ultima = Math.max(1, Math.ceil(r.total / r.porPagina));
      if (r.total > 0 && pagina > ultima) { setPagina(ultima); return; }
      setListado(r);
      setError(null);
    } catch (e) {
      if (pedido === ultimoPedido.current) setError(extractErrorMessage(e));
    } finally {
      if (pedido === ultimoPedido.current) setCargando(false);
    }
  }, [filtro, pagina]);

  useEffect(() => { void cargar(); }, [cargar]);

  const filtrar = (e: FormEvent) => {
    e.preventDefault();
    setPagina(1);
    setFiltro(texto.trim());
  };

  const baseDe = (sku: string) => listado?.bases.find((b) => b.sku === sku);
  const alternar = (base: string) => setElegidas((previas) => {
    const siguientes = new Map(previas);
    const info = baseDe(base);
    if (siguientes.has(base)) siguientes.delete(base);
    else if (info) siguientes.set(base, info);
    return siguientes;
  });
  const elegirTodas = () => setElegidas(new Map((listado?.bases ?? []).map((b) => [b.sku, b])));

  const aSincronizar = [...elegidas.values()];
  const publicacionesAEscribir = aSincronizar.reduce((t, b) => t + b.publicacionesQueSeEscriben, 0);
  const packsAEncender = aSincronizar.reduce((t, b) => t + b.packs.length, 0);

  /** Cada SKU base con sus packs confirmados, de a lotes (sincronizarEnLotes). */
  const sincronizar = async () => {
    if (corriendo || aSincronizar.length === 0) return;
    setConfirmar(false);
    setResumen(null);
    setResultados({});
    const hecho = await sincronizarEnLotes(
      aSincronizar,
      (items) => api.post<ResultadoLote>(`${URL}/habilitar-packs-lote`, { items: items.map((b) => ({ sku: b.sku, packs: b.packs })) }),
      { seguir: () => montado.current, progreso: setProgreso, resultados: setResultados },
    );
    if (hecho === null) return;
    setProgreso(null);
    setElegidas(new Map());
    setResumen(hecho);
    await cargar();
  };

  const filas = listado?.filas ?? [];
  const identidad = useIdentidades(filas.map((f) => f.sku));
  const paginas = listado ? Math.max(1, Math.ceil(listado.total / listado.porPagina)) : 1;
  // Los packs que empezaron a sincronizar salen de la lista: la columna sólo se muestra si queda alguna fila con resultado.
  const conResultados = filas.some((f) => resultados[f.base]);
  const columnas = (esAdmin ? 1 : 0) + 4 + (conResultados ? 1 : 0);
  const titulo = 'Packs sin sincronizar de SKU que ya sincronizan';

  return (
    <section aria-label={titulo} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-800">{titulo}</h3>
          {listado && <Badge tone={listado.total > 0 ? 'amber' : 'green'}>{plural(listado.total, 'pack', 'packs')}</Badge>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => void cargar()} loading={cargando} disabled={corriendo}>Actualizar</Button>
      </div>
      <p className="text-sm text-slate-600 max-w-4xl">
        Packs nuevos de SKU que ya sincronizan, por ejemplo los que aparecen cuando se corrige la columna SKU de la planilla
        02_CODIGOS. Hasta que los sincronices, Mercado Libre se queda con el stock viejo de esas publicaciones. Los que pasan de
        un SKU que sincroniza a su pack empiezan solos; acá quedan los demás. Los packs de un mismo SKU se sincronizan juntos.
      </p>
      {!esAdmin && <p className="text-xs text-slate-500">Sólo un administrador puede sincronizarlos.</p>}

      <form onSubmit={filtrar} className="flex flex-wrap items-end gap-2">
        <input
          type="search"
          aria-label="Buscar pack o SKU base"
          placeholder="Pack, SKU base o descripción"
          className="w-64 rounded border border-slate-300 px-2 py-1 text-sm"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <Button type="submit" variant="secondary" size="sm" disabled={corriendo}>Filtrar</Button>
        {esAdmin && listado && listado.bases.length > 0 && (
          <>
            <Button type="button" variant="ghost" size="sm" onClick={elegirTodas} disabled={corriendo}>
              {listado.bases.length === 1 ? 'Elegir el SKU base filtrado' : `Elegir los ${n(listado.bases.length)} SKU base filtrados`}
            </Button>
            {elegidas.size > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setElegidas(new Map())} disabled={corriendo}>Quitar la selección</Button>
            )}
            <Button type="button" size="sm" onClick={() => setConfirmar(true)} disabled={corriendo || elegidas.size === 0}>
              {`Sincronizar ${n(elegidas.size)} SKU base`}
            </Button>
          </>
        )}
      </form>

      {error && <p role="alert" className="text-sm text-red-600">No se pudo cargar la lista: {error}</p>}

      {progreso && (
        <p role="status" className="text-sm text-slate-700">
          <span>{`Sincronizando lote ${progreso.actual} de ${progreso.total}…`}</span>
          {progreso.esperando && <span className="block text-amber-700">Esperando para seguir (límite de pedidos)…</span>}
          <span className="block text-xs text-slate-500">No cierres esta pantalla hasta que termine.</span>
        </p>
      )}
      {resumen && (
        <p role="status" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-700">{`Listo: ${resumen}.`}</p>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              {esAdmin && <th className="py-1 px-2 font-medium"><span className="sr-only">Elegir</span></th>}
              <th className="py-1 px-2 font-medium">Pack</th>
              <th className="py-1 px-2 font-medium">SKU base</th>
              <th className="py-1 px-2 font-medium" title="Stock en Mercado Libre según la última sincronización de publicaciones → lo que se publicaría">
                Publicaciones (hoy → se publicaría)
              </th>
              <th className="py-1 px-2 font-medium">Avisos</th>
              {conResultados && <th className="py-1 px-2 font-medium">Resultado</th>}
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr><td colSpan={columnas} className="py-3 px-2 text-slate-500">{cargando ? 'Cargando…' : 'No hay packs sin sincronizar de SKU que ya sincronizan'}</td></tr>
            )}
            {filas.map((f) => {
              const r = resultados[f.base];
              return (
                <tr key={f.sku} className="border-b border-slate-100 align-top">
                  {esAdmin && (
                    <td className="py-1 px-2">
                      <input
                        type="checkbox"
                        aria-label={`Elegir ${etiquetaArticulo(f.sku, identidad(f.sku))}`}
                        checked={elegidas.has(f.base)}
                        disabled={corriendo}
                        onChange={() => alternar(f.base)}
                      />
                    </td>
                  )}
                  <td className="py-1 px-2">
                    <div className="font-medium"><CodigoArticulo sku={f.sku} /></div>
                    <div className="text-xs text-slate-500">{f.descripcion ?? '—'}</div>
                  </td>
                  <td className="py-1 px-2 whitespace-nowrap">
                    <CodigoArticulo sku={f.base} /> <span className="text-xs text-slate-500">{`x${f.unidadesPorPack.toLocaleString('es-AR')}`}</span>
                  </td>
                  <td className="py-1 px-2">
                    {f.publicaciones.length === 0 ? '—' : (
                      <ul className="space-y-0.5 text-xs">
                        {f.publicaciones.map((p) => (
                          <li key={`${p.mlaId}-${p.variantId ?? ''}`} className={p.seEscribe ? '' : 'text-amber-700'} title={p.titulo ?? undefined}>
                            {p.seEscribe
                              ? `${nombrePublicacion(p)}: hoy ${n(p.stockMl)} → ${n(p.sePublica)}`
                              : `${nombrePublicacion(p)}: No se actualiza — ${p.motivo ?? ''}`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-1 px-2">
                    {f.avisos.length > 0 ? (
                      <ul className="space-y-0.5 text-xs text-amber-700">{f.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
                    ) : '—'}
                  </td>
                  {conResultados && <td className={`py-1 px-2 text-xs ${r ? COLOR_RESULTADO[r.estado] : ''}`}>{r?.mensaje ?? ''}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {listado && paginas > 1 && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Button variant="ghost" size="sm" onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={pagina <= 1 || corriendo}>Anterior</Button>
          <span>{`Página ${pagina} de ${paginas}`}</span>
          <Button variant="ghost" size="sm" onClick={() => setPagina((p) => Math.min(paginas, p + 1))} disabled={pagina >= paginas || corriendo}>Siguiente</Button>
        </div>
      )}

      <Modal open={confirmar} onClose={() => setConfirmar(false)} title="Sincronizar packs" size="md">
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            {`Empiezan a sincronizar ${plural(packsAEncender, 'pack', 'packs')} de ${plural(aSincronizar.length, 'SKU base', 'SKU base')}: `}
            <strong>{`se va a escribir en Mercado Libre el stock de ${plural(publicacionesAEscribir, 'publicación', 'publicaciones')}`}</strong>.
          </p>
          <p className="text-slate-600">
            El stock se escribe en los próximos minutos, de a una publicación. Las que dicen &quot;No se actualiza&quot; quedan con el
            stock viejo hasta corregir lo que dice cada una.
          </p>
          <div className="flex justify-end gap-3 pt-3 border-t">
            <Button variant="ghost" onClick={() => setConfirmar(false)}>Cancelar</Button>
            <Button onClick={() => void sincronizar()}>Confirmar y sincronizar</Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
